import Color from 'color';
import { twipsToPx } from '@tencent/alloy-editor';
import {
    ColorBackType,
    IColorAnalysisTask,
    IColorAnalysisResult,
    IElementBack,
    ISlideBack,
    ITextColorBack,
} from './color-base-analyzer.interface';
import { ITextElementBoxNode } from '../../slide-content-enumerator/enumerator-types.interface';
import { TextColorContrastResult } from '../color/result/text-color-contrast-result';

export function getImageTextColorContrast(task: IColorAnalysisTask): Promise<IColorAnalysisResult> | undefined {
    const { boxNode, textColor, textBack } = task;
    if (textBack.image) {
        const src = textBack.image?.src;
        if (!src) {
            return undefined;
        }

        const { width, height } = getTextRect(textBack);
        const { x, y } = getTextPosition(boxNode, textBack);

        const canvas = document.createElement('canvas');
        const ctx = (canvas as HTMLCanvasElement).getContext('2d');
        const img = new Image();
        const res = new Promise<IColorAnalysisResult>((resolve) => {
            img.onload = () => {
                const defaultRect = { width: 1, height: 1 };
                const text = boxNode?.textBox.rect ?? defaultRect;
                const sx = (x / width) * img.width; // 相对于原始图片的位置
                const sy = (y / height) * img.height; // 相对于原始图片的位置
                const sWidth = (twipsToPx(text.width) / width) * img.width;
                const sHeight = (twipsToPx(text.height) / height) * img.height;
                const dWidth = (sWidth / sHeight) * 150 > 300 ? 300 : (sWidth / sHeight) * 150;
                const dHeight = (sHeight / sWidth) * dWidth;
                ctx?.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, dWidth, dHeight);

                const imageData = ctx?.getImageData(0, 0, dWidth, dHeight);
                const data: any = imageData?.data ?? [];

                let sumR = 0;
                let sumG = 0;
                let sumB = 0;
                for (let i = 0; i < data.length; i += 4) {
                    sumR += data[i];
                    sumG += data[i + 1];
                    sumB += data[i + 2];
                }

                const total = data.length / 4;
                const contrast = TextColorContrastResult.getColorsContrast(
                    Color.rgb(sumR / total, sumG / total, sumB / total),
                    textColor,
                );
                resolve({
                    boxNode,
                    textColor,
                    textBack,
                    contrast,
                });
            };
            img.crossOrigin = 'Anonymous';
            img.src = src;
        });
        return res;
    }
    return undefined;
}

/**
 * 获取文本相对于 model 的位置
 */
function getTextPosition(textNode: ITextElementBoxNode, back: ITextColorBack) {
    let backRect: { left: number; top: number; id?: string | null };
    if (back.type === ColorBackType.ELEMENT) {
        const backElement = (back as IElementBack).elementNode.element;
        const { left, top } = backElement.props.style;
        backRect = { left, top, id: backElement.id };
    } else if (back.type === ColorBackType.SLIDE_BACKGROUND) {
        backRect = { left: 0, top: 0, id: (back as ISlideBack).slideModel.id };
    } else {
        backRect = { left: 0, top: 0 };
    }

    const defaultRect = { x: 0, y: 0, width: 1, height: 1 };
    const text = textNode?.textBox.rect ?? defaultRect;
    const textLine = textNode?.textBox.parentBox?.rect ?? defaultRect;
    const paragraph = textNode?.textBox.parentBox?.parentBox?.rect ?? defaultRect;
    const doc = textNode?.textBox.parentBox?.parentBox?.parentBox?.rect ?? defaultRect;

    // 如果文字就在图片元素上，可以简便计算
    if (backRect.id === textNode.element.id) {
        const x = twipsToPx(doc.x + paragraph.x + textLine.x + text.x);
        const y = twipsToPx(doc.y + paragraph.y + textLine.y + text.y);
        return { x, y };
    }

    // 如果文字和图片分属两个元素
    const textX = twipsToPx(doc.x + paragraph.x + textLine.x + text.x);
    const textY = twipsToPx(doc.y + paragraph.y + textLine.y + text.y);
    const textModelRect = textNode?.element.props.style;
    const x = textX + textModelRect.left - backRect.left;
    const y = textY + textModelRect.top - backRect.top;
    return { x, y };
}

/**
 * 获取背景 model 的宽高
 */
function getTextRect(back: ITextColorBack) {
    if (back.type === ColorBackType.ELEMENT) {
        const backElement = (back as IElementBack).elementNode.element;
        const { width, height } = backElement.props.style;
        return { width, height };
    }
    if (back.type === ColorBackType.SLIDE_BACKGROUND) {
        const { slideWidth, slideHeight } = (back as ISlideBack).slideModel.presentationRef?.props ?? {};
        return { width: slideWidth ?? 0, height: slideHeight ?? 0 };
    }
    return { width: 0, height: 0 };
}
